import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { useAPISettings } from "@src/data-hooks/general-settings/settings.service";
import { usePingedCloudFunction } from "@src/data-hooks/generic/usePingedCloudFunction";
import { UpdateUnitInput, updatePropertyUnit } from "@src/data-hooks/unit/unit.service";
import { isFieldEnabled } from "@src/utils/settings/settings.utils";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FirebaseDataDateField } from "to-be-removed/LocalData/General/DataObjectField/Generic/FirebaseDataDateField";
import { ConfirmationPopup } from "../../../../components/deprecated/ConfirmationPopup/ConfirmationPopup";
import { Icon } from "../../../../components/deprecated/Icon/Icon";
import { Popup } from "../../../../components/deprecated/Popup/Popup";
import { PopupContainer } from "../../../../components/deprecated/PopupContainer/PopupContainer";
import { Text } from "../../../../components/deprecated/Text/Text";
import { ToastUtilities } from "../../../../libs/react-toastify/ToastUtilities";
import { FirebaseInfoFieldField } from "../../../../to-be-removed/LocalData/General/DataObjectField/CustomFields/PropertyFields";
import { DataObjectField } from "../../../../to-be-removed/LocalData/General/DataObjectField/DataObjectField";
import { DataObjectHelpers } from "../../../../to-be-removed/LocalData/General/DataObjects/DataObjectHelpers";
import { LocalData } from "../../../../to-be-removed/LocalData/General/LocalData";
import { UnitData } from "../../../../to-be-removed/LocalData/Property/Units/UnitData";
import { PropertyFieldsSetting } from "../../../Companies/Company/Properties/PropertyFieldsSettings/PropertyFieldsSetting";
import styles from "./TenantKeyValues.module.css";
import { Lock } from "@src/assets/images/icons/untitled_ui_icons";
import TypographyText from "@src/components/ui-components/TypographyText/TypographyText";
export const tenantFinanceKeys = [
  "legalEntity",
  "incomeData",
  "vatApplicable",
  "receivesEhfInvoice",
  "contractConclusionDate",
  "contractStartDate",
  "contractEndDate",
  "runningRenewal",
  "optionDuration",
  "optionDeadline",
  "indexRegulation",
  "fixedRegulation",
  "regulationInterval",
  "lastRegulation",
  "jointArea",
  "guarantee",
  "waterRent",
  "electricityRent",
  "accountNumber",
];


const getLockedFields = (integrations: { name: string; logo: string; supportLink?: string; supportText?: string; }[]) => {
  const lockedFields: string[] = [];
  integrations.forEach((integration) => {
    switch (integration.name) {
      case "fazile":
        lockedFields.push(
          "name",
          "orgNumber",
          "postAddress",
          "contactEmail",
          "contactPhone",
          "area",
          "incomeData",
          "vatApplicable",
          "contractStartDate",
          "contractEndDate",
          "runningRenewal",
          "optionDeadline",
          "optionDuration",
          "guarantee"
        );
        break;
      case "fenistra":
        lockedFields.push(
          "name",
          "orgNumber",
          "postAddress",
          "area",
          "contractConclusionDate",
          "contractStartDate",
          "contractEndDate",
          "note"
        );
        break;
      case "hybel":
        lockedFields.push(
          "name",
          "postAddress",
          "area",
          "unitNumber",
          "streetNumber",
          "contactEmail",
          "contactPhone",
          "annualRent",
          "contractStartDate",
          "contractEndDate",
        );
        break;
    }
  });
  return lockedFields;
};

type Props = {
  propertyID: string;
  tenant: UnitData;
  hideSettings: () => void;
  savedUnit: () => void;
  integrations: { name: string; logo: string; supportLink?: string; supportText?: string; }[];
};


export const TenantKeySettings = ({ propertyID, tenant, hideSettings, savedUnit, integrations, }: Props) => {

  const { settings } = useAPISettings({ propertyID: tenant.property?.id });
  const [data, setData] = useState<{ [key: string]: any }>({});
  const [, setLastDate] = useState(new Date());
  const $updatePropertyUnit = useMutation(updatePropertyUnit);
  const _ = usePingedCloudFunction("updateTenantInfoFields");

  const adminCompany = tenant.property?.companies.find((company) => company.members.getData(LocalData.currentUser.id)?.admin);
  const allFields: DataObjectField<any, any>[] = DataObjectHelpers.unit.allFields();
  const financeModuleActivated = tenant.property?.moduleIsActivated("tenantEconomy") ?? false;

  let fields = [
    DataObjectHelpers.unit.name,
    ...allFields.filter((field) => {
      if (field.key === "incomeData" || field.key === "name") {
        return false;
      }
      return isFieldEnabled(field.key, "unit", financeModuleActivated, settings?.data);
    }),
  ];

  useEffect(() => {
    const newData: { [key: string]: any } = {};
    for (const field of fields) {
      newData[field.key] = field.fullDecode(field.dataGetter(tenant));
    }
    setData(newData);
    setLastDate(new Date());
  }, [tenant]);

  const updateField = (field: string, value: any) => {
    setData({ ...data, [field]: value });
  };

  const saveSettings = () => {
    if (!tenant.property) return;

    const input: UpdateUnitInput = {
      propertyId: tenant.property?.id,
      unitId: tenant.id,
      data: {},
    };
    for (const field of fields) {
      const fieldValue = data[field.key];
      if (field instanceof FirebaseInfoFieldField) {
        if (typeof fieldValue === "object" && fieldValue.value !== undefined) {
          input.data[field.key] = field.fullEncode(fieldValue);
        }
      } else if (fieldValue !== undefined) {
        if (field instanceof FirebaseDataDateField && fieldValue instanceof Date) {
          input.data[field.key] = { value: fieldValue.getTime() };
        } else {
          input.data[field.key] = { value: field.fullEncode(fieldValue) };
        }
      } else {
        input.data[field.key] = { value: null };
      }
    }

    const promise = $updatePropertyUnit
      .mutateAsync(input)
      .then(() => {
        ToastUtilities.presentSuccess(t("main.saved"));

        tenant.loadInfoFields(() => {
          hideSettings();
          savedUnit();
        });
      })
      .catch((error) => {
        ToastUtilities.presentServerError(error);
      });

    ToastUtilities.presentSavingToast(promise);
  };

  const updateTenantArchived = () => {
    const isArchived = tenant.archived;
    let data = {};
    if (isArchived) {
      data = {
        archived: false,
        archiveDate: null,
      };
    } else {
      data = {
        archived: true,
        archiveDate: new Date(),
      };
    }
    ToastUtilities.presentSavingToast(
      tenant.saveUpdate(data).then(() => {
        tenant.archived = !isArchived;
        hideSettings();
        savedUnit();
      })
    );
  };

  const administrateFields = () => {
    if (adminCompany) {
      Popup.present((id) => (
        <PopupContainer
          className={styles.fieldPopup}
          id={id}
          children={<PropertyFieldsSetting company={adminCompany} fieldInformation="unit" />}
          centered
          maxHeight="90%"
        />
      ));
    }
  };


  return (
    <div>
      <Icon onClick={() => hideSettings()} name="close" float="right" />
      <table className={styles.table}>
        <tbody>
          {fields.map((field) => {
            return (
              <tr key={field.key}>
                <td>
                  <Text size="md" leadingIcon={field.iconName} marginRight={16}>
                    {field.title}
                  </Text>
                </td>
                <td>
                  {field.inputElement?.(data[field.key], (value) => updateField(field.key, value), {
                    width: "300px",
                    disabled: $updatePropertyUnit.isLoading || getLockedFields(integrations).includes(field.key),
                    propertyID,
                  })}
                </td>
                <td>
                  {getLockedFields(integrations).includes(field.key) && (
                      <TypographyText style={{ display: 'flex', justifyContent: 'start', alignItems: 'center', marginLeft: '8px', flexWrap: 'wrap', padding: '8px 0', gap: '6px', textTransform: 'capitalize' }}>
                        {(() => {
                          const integration = integrations.find((integration) => getLockedFields([integration]).includes(field.key));
                          return (
                            <>
                              {integration?.logo && <img src={integration.logo} alt={`${integration.name} logo`} width="16px" />}
                              {integration?.name}
                            </>
                          );
                        })()}
                      </TypographyText>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Button marginTop={32} fullWidth onClick={() => saveSettings()} loading={$updatePropertyUnit.isLoading}>
        {t("main.save")}
      </Button>
      <Button
        marginTop={16}
        fullWidth
        leadingIcon={tenant.archived ? "unarchive" : "archive"}
        type="secondary"
        disabled={$updatePropertyUnit.isLoading}
        onClick={() => {
          Popup.present((id) => (
            <ConfirmationPopup
              title={tenant.archived ? t("main.unarchiveTenant") : t("main.archiveTenant")}
              onConfirmClicked={() => updateTenantArchived()}
              description={tenant.archived ? t("main.confirmUnarchiveTenant") : t("main.confirmArchiveTenant")}
              id={id}
            />
          ));
        }}
      >
        {tenant.archived ? t("main.unarchiveTenant") : t("main.archiveTenant")}
      </Button>
      {adminCompany && (
        <Button
          type="text"
          leadingIcon="help"
          marginTop={16}
          onClick={() => administrateFields()}
          disabled={$updatePropertyUnit.isLoading}
          style={{
            fontStyle: "italic",
          }}
        >
          {t("main.editInfoFieldsFromTenantText")}
        </Button>
      )}
    </div>
  );
};
